import { Box, Button as MuiButton, MenuItem, Select as MuiSelect, TextField, ThemeProvider } from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';

import { Switch as CommonSwitch } from '@/components/common/Switch';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Check,
  Chip,
  Divider,
  Eyebrow,
  Field,
  Icon,
  IconButton,
  Meta,
  NavItem,
  NavSection,
  Segmented,
  Sheet,
  SheetHeader,
  Stat,
  StatRow,
  TextInput,
  Tile,
} from '@/components/ds';
import { useAppThemeContext } from '@/context/AppThemeProvider';
import { createAppTheme, Mode, MODE_LABELS, MODES, THEME_LABELS, THEME_NAMES, ThemeName, tx } from '@/theme';

// /dev/library: every Hauser design-system component, in every state, in Light, Natural and Dark side by side.
// It is the review surface for the system; a screen only uses what is drawn here. Development builds only.

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
    <Eyebrow>{title}</Eyebrow>
    {children}
  </Box>
);

const Row = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>{children}</Box>
);

const Specimen = () => {
  const [seg, setSeg] = useState('summary');
  const [segSm, setSegSm] = useState('month');
  const [checked, setChecked] = useState(true);
  const [unchecked, setUnchecked] = useState(false);
  const [chips, setChips] = useState<string[]>(['roof']);
  const [nav, setNav] = useState('condition');
  const [sw, setSw] = useState(true);
  const toggleChip = (c: string) =>
    setChips((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      <Section title="Buttons">
        <Row>
          <Button variant="primary">Request quotes</Button>
          <Button>Share</Button>
          <Button variant="ghost">Cancel</Button>
          <Button variant="danger">Remove</Button>
        </Row>
        <Row>
          <Button variant="primary" icon="plus">
            Add property
          </Button>
          <Button icon="download">Report</Button>
          <Button endIcon="chev-right" variant="ghost">
            Details
          </Button>
          <Button disabled>Disabled</Button>
        </Row>
        <Row>
          <Button size="sm" variant="primary">
            Save
          </Button>
          <Button size="sm">Compact</Button>
          <Button size="sm" variant="ghost">
            Ghost
          </Button>
        </Row>
      </Section>

      <Section title="Icon buttons">
        <Row>
          <IconButton icon="search" label="Search" />
          <IconButton icon="bell" label="Notifications" />
          <IconButton icon="sidebar" label="Show sidebar" on />
          <IconButton icon="more" label="More" variant="ghost" />
          <IconButton icon="x" label="Close" size="sm" />
          <IconButton icon="download" label="Download" size="sm" variant="ghost" />
        </Row>
      </Section>

      <Section title="Fields">
        <Field label="Property address" hint="Street, city and ZIP">
          <TextInput placeholder="1234 Lita Place" fullWidth />
        </Field>
        <Field label="Search" aside={<Meta>⌘K</Meta>}>
          <TextInput icon="search" placeholder="Search documents" fullWidth />
        </Field>
        <Field label="Note">
          <TextInput placeholder="Write a note for your client" multiline minRows={2} fullWidth />
        </Field>
        <Field label="With error" hint="Enter a valid amount">
          <TextInput defaultValue="$12,o00" error fullWidth />
        </Field>
      </Section>

      <Section title="Segmented">
        <Segmented
          label="View"
          value={seg}
          onChange={setSeg}
          options={[
            ['summary', 'Summary'],
            ['condition', 'Condition'],
            ['context', 'Context'],
          ]}
        />
        <Segmented
          size="sm"
          value={segSm}
          onChange={setSegSm}
          options={[
            ['week', 'Week'],
            ['month', 'Month'],
            ['year', 'Year'],
          ]}
        />
      </Section>

      <Section title="Chips and badges">
        <Row>
          {['roof', 'plumbing', 'electrical', 'hvac'].map((c) => (
            <Chip key={c} on={chips.includes(c)} onClick={() => toggleChip(c)} count={c === 'roof' ? 4 : undefined}>
              {c === 'hvac' ? 'HVAC' : c[0].toUpperCase() + c.slice(1)}
            </Chip>
          ))}
        </Row>
        <Row>
          <Badge value={3} />
          <Badge value={12} tone="neutral" />
          <Chip icon="clock">Read only</Chip>
        </Row>
      </Section>

      <Section title="Check and switch">
        <Check checked={checked} onChange={setChecked}>
          Include photos
        </Check>
        <Check checked={unchecked} onChange={setUnchecked}>
          Email my client
        </Check>
        <Check checked={false} onChange={() => undefined} disabled>
          Disabled
        </Check>
        <Row>
          <CommonSwitch checked={sw} onChange={(e) => setSw(e.target.checked)} />
          <CommonSwitch checked={false} />
          <CommonSwitch checked disabled />
        </Row>
      </Section>

      <Section title="Sheet, header and cards">
        <Sheet>
          <SheetHeader eyebrow="Condition" title="Roof" sub="4 findings · inspected Oct 12" onClose={() => undefined} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Card title="Flashing at chimney" step="1" aside="$1,200–1,800">
              <Box sx={{ fontSize: 13, fontWeight: 500, color: tx.muted }}>
                Rust and gaps where the chimney meets the roof. Reseal before the rainy season.
              </Box>
            </Card>
            <Card title="Cost and time">
              <StatRow>
                <Stat value="$3,400" label="Estimate" />
                <Stat value="2 days" label="Work" />
                <Stat value="72" label="Score" />
              </StatRow>
            </Card>
          </Box>
          <Divider sx={{ my: '16px' }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="ghost">Later</Button>
            <Button variant="primary">Request quotes</Button>
          </Box>
        </Sheet>
      </Section>

      <Section title="Tiles">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <Tile icon="calc" label="Calculators" sub="Mortgage, HELOC" />
          <Tile icon="calendar" label="Calendar" on />
          <Tile icon="contact" label="Vendors" />
          <Tile icon="book" label="Records" sub="12 documents" />
        </Box>
      </Section>

      <Section title="Navigation">
        <Sheet sx={{ p: '12px' }}>
          <NavSection label="Due diligence">
            {[
              ['summary', 'Summary', 'dash'],
              ['condition', 'Condition', 'home'],
              ['context', 'Context', 'pin'],
              ['repair', 'Repair planner', 'pen'],
              ['market', 'Market', 'perform'],
            ].map(([k, l, i]) => (
              <NavItem
                key={k}
                label={l}
                icon={<Icon name={i as 'home'} size={16} />}
                active={nav === k}
                badge={k === 'repair' ? 2 : undefined}
                onClick={() => setNav(k)}
              />
            ))}
          </NavSection>
          <Divider sx={{ my: '10px' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '6px' }}>
            <Avatar name="James Choe" />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 13, fontWeight: 600 }}>James Choe</Box>
              <Meta>Advisor</Meta>
            </Box>
          </Box>
        </Sheet>
      </Section>

      <Section title="MUI controls (themed)">
        <Row>
          <MuiButton>Contained</MuiButton>
          <MuiButton variant="outlined">Outlined → tonal</MuiButton>
          <MuiButton variant="text">Text → ghost</MuiButton>
        </Row>
        <TextField placeholder="MUI TextField" fullWidth />
        <MuiSelect value="sfr" fullWidth>
          <MenuItem value="sfr">Single family</MenuItem>
          <MenuItem value="condo">Condo</MenuItem>
        </MuiSelect>
      </Section>
    </Box>
  );
};

const ThemeColumn = ({ name }: { name: ThemeName }) => {
  const theme = useMemo(() => createAppTheme(name), [name]);
  return (
    <ThemeProvider theme={theme}>
      <Box
        data-theme={name}
        sx={{
          flex: 1,
          minWidth: 340,
          p: '24px',
          backgroundColor: tx.ground,
          backgroundImage: 'var(--tx-ground-light)',
          color: tx.text,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}>
        <Box sx={{ fontSize: 13, fontWeight: 600, color: tx.muted }}>{THEME_LABELS[name]}</Box>
        <Specimen />
      </Box>
    </ThemeProvider>
  );
};

export const DesignLibrary = () => {
  const { mode, setMode } = useAppThemeContext();
  const [layout, setLayout] = useState<'all' | 'one'>('all');

  return (
    <Box sx={{ minHeight: '100dvh', color: tx.text }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', p: '20px 24px', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Eyebrow>Hauser design system</Eyebrow>
          <Box sx={{ fontSize: 28, fontWeight: 300, letterSpacing: '-0.035em' }}>Component library</Box>
        </Box>
        <Segmented
          label="Layout"
          value={layout}
          onChange={setLayout}
          options={[
            ['all', 'All themes'],
            ['one', 'App mode'],
          ]}
        />
        <Segmented
          label="Mode"
          value={mode}
          onChange={setMode}
          options={MODES.map((m) => [m, MODE_LABELS[m]] as [Mode, string])}
        />
      </Box>
      {layout === 'all' ? (
        <Box sx={{ display: 'flex', overflowX: 'auto' }}>
          {THEME_NAMES.map((n) => (
            <ThemeColumn key={n} name={n} />
          ))}
        </Box>
      ) : (
        <Box sx={{ maxWidth: 520, mx: 'auto', p: '24px' }}>
          <Specimen />
        </Box>
      )}
    </Box>
  );
};

export default DesignLibrary;
